/**
 * @file with-throttle-retry.ts
 * @description Wrapper de retry 429 unifié autour d'un `fetch` (ou de tout appel
 * renvoyant une `Response`).
 *
 * Invariants (standard 429 du SDK) :
 *  - **Détection par status HTTP 429 uniquement** (jamais par `error.message`).
 *  - **Retry-After honoré** (secondes + `x-ms-retry-after-ms`), clampé [0, 30s].
 *  - **Fallback** = backoff exponentiel + jitter ±20% (cf. `computeThrottleDelayMs`).
 *  - **Bornage strict** : `maxRetries` tentatives de retry (défaut 3), pas de boucle infinie.
 *  - **Épuisement** → lève une {@link ThrottleError} typée (status 429, isThrottle).
 *  - **Logs** : `logger.warn` sur chaque retry intermédiaire ; `logger.error` uniquement à l'abandon.
 *  - **5xx optionnel** (`retryOn5xx`) : certains sites rejouent aussi les erreurs serveur.
 *
 * Note : ce wrapper retente sur le **status HTTP de la réponse**. Il ne capture PAS
 * les erreurs réseau (`fetch` qui rejette) — chaque appelant garde sa propre sémantique
 * réseau / 401 autour de lui.
 */
import { logger } from "../logs/core";
import { LOGS } from "../logs/logConfig";
import {
    computeThrottleDelayMs,
    type ComputeThrottleDelayOptions,
} from "./compute-throttle-delay";
import { ThrottleError } from "./throttle-error";

export type WithThrottleRetryOptions = ComputeThrottleDelayOptions & {
    /** Libellé/endpoint pour les logs et la ThrottleError. */
    endpoint: string;
    /** Catégorie(s) de log. */
    logCategory: LOGS | LOGS[];
    /** Nombre maximum de tentatives de retry sur 429. Défaut : 3. */
    maxRetries?: number;
    /** Retenter aussi les réponses 5xx (erreurs serveur transitoires). Défaut : false. */
    retryOn5xx?: boolean;
    /** Fonction d'attente injectable (tests). Défaut : setTimeout. */
    sleep?: (ms: number) => Promise<void>;
};

const DEFAULT_MAX_RETRIES = 3;

const defaultSleep = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

/** True si le status doit déclencher un retry, selon les options. */
function isRetryableStatus(status: number, retryOn5xx: boolean): boolean {
    return status === 429 || (retryOn5xx && status >= 500);
}

/**
 * Exécute `doFetch`, rejoue sur 429 (et 5xx si `retryOn5xx`) jusqu'à `maxRetries`
 * tentatives, puis lève une {@link ThrottleError} si le throttle persiste.
 *
 * @param doFetch - Fabrique l'appel et renvoie la `Response`. Rappelée à chaque tentative.
 * @param opts    - Configuration (endpoint, logs, bornes, jitter…).
 * @returns La première `Response` non-throttlée (status hors 429/5xx-retryable).
 * @throws {ThrottleError} si le 429 persiste après `maxRetries` tentatives.
 */
export async function withThrottleRetry(
    doFetch: () => Promise<Response>,
    opts: WithThrottleRetryOptions,
): Promise<Response> {
    const maxRetries = opts.maxRetries ?? DEFAULT_MAX_RETRIES;
    const retryOn5xx = opts.retryOn5xx ?? false;
    const sleep = opts.sleep ?? defaultSleep;
    const { endpoint, logCategory } = opts;

    let attempt = 0;
    let response: Response;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        response = await doFetch();

        if (
            isRetryableStatus(response.status, retryOn5xx) &&
            attempt < maxRetries
        ) {
            attempt++;
            const delayMs = computeThrottleDelayMs(response, attempt, opts);

            // Tentatives intermédiaires = logger.warn (un retry suit) ; logger.error réservé à l'abandon.
            logger.warn(
                logCategory,
                `${response.status} (throttle/transient) — retry ${attempt}/${maxRetries} in ${delayMs}ms (${endpoint})`,
            );

            await sleep(delayMs);
            continue;
        }

        break;
    }

    // Throttle toujours présent après épuisement des tentatives → erreur typée.
    if (response.status === 429) {
        logger.error(
            logCategory,
            `Received 429 Too Many Requests after ${maxRetries} retries (${endpoint}) — giving up`,
        );
        throw new ThrottleError(endpoint, maxRetries);
    }

    logger.info(
        logCategory,
        `withThrottleRetry: resolved ${endpoint} with status ${response.status}`,
    );
    return response;
}
